"use client";

import { useState, useEffect } from "react";
import { 
  Headphones, 
  Search, 
  Plus, 
  TrendingUp, 
  PhoneCall, 
  Calendar, 
  Award, 
  FileText, 
  Layers, 
  RefreshCw 
} from "lucide-react";
import { TelecallingLead } from "@/lib/demos/telecallingDb";
import { TelecallingTopBar } from "./TelecallingTopBar";
import { TelecallingKanban } from "./TelecallingKanban";
import { TelecallingStageModal } from "./TelecallingStageModal";
import { TelecallingDetailModal } from "./TelecallingDetailModal";
import { CampaignProposalModal } from "./CampaignProposalModal";

interface TelecallingCrmDashboardProps {
  initialLeads?: TelecallingLead[];
  initialStats?: {
    totalLeads: number;
    connectedCalls: number;
    demosScheduled: number;
    dealsWon: number;
    totalRetainerRevenueInr: number;
    totalRetainerRevenueUsd: number;
  };
}

export function TelecallingCrmDashboard({
  initialLeads = [],
  initialStats = {
    totalLeads: 0,
    connectedCalls: 0,
    demosScheduled: 0,
    dealsWon: 0,
    totalRetainerRevenueInr: 0,
    totalRetainerRevenueUsd: 0,
  },
}: TelecallingCrmDashboardProps) {
  const [leads, setLeads] = useState<TelecallingLead[]>(initialLeads);
  const [stats, setStats] = useState(initialStats);
  const [currency, setCurrency] = useState<"INR" | "USD">("INR");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isResetting, setIsResetting] = useState(false);

  // Modals state
  const [selectedDetailLead, setSelectedDetailLead] = useState<TelecallingLead | null>(null);
  const [stageModalLead, setStageModalLead] = useState<{ lead: TelecallingLead; targetStage: string } | null>(null);
  const [isProposalModalOpen, setIsProposalModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  async function handleResetDemo() {
    setIsResetting(true);
    try {
      const res = await fetch("/api/demos/telecalling/reset", { method: "POST" });
      const data = await res.json();
      if (data.success) {
        setLeads(data.leads || []);
        setStats(data.stats || initialStats);
      }
    } catch (err) {
      console.error("[Reset Demo Error]", err);
    } finally {
      setIsResetting(false);
    }
  }

  const sym = currency === "USD" ? "$" : "₹";
  const revenueTotal = currency === "USD" ? stats.totalRetainerRevenueUsd : stats.totalRetainerRevenueInr;

  async function fetchLeads() {
    setIsLoading(true);
    try {
      const res = await fetch("/api/demos/telecalling/leads");
      const data = await res.json();
      if (data.success) {
        setLeads(data.leads);
        setStats(data.stats);
      }
    } catch (err) {
      console.error("[Telecalling Leads Refresh Error]", err);
    } finally {
      setIsLoading(false);
    }
  }

  // Handle stage transition from modal
  async function handleConfirmStageTransition(updates: Partial<TelecallingLead>) {
    if (!stageModalLead) return;

    try {
      const res = await fetch(`/api/demos/telecalling/leads/${stageModalLead.lead.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });

      const data = await res.json();
      if (data.success) {
        await fetchLeads();
      }
    } catch (error) {
      console.error("[Stage Transition PATCH Error]", error);
    }
  }

  // Handle direct drag-and-drop move between non-modal stages
  async function handleDirectStageMove(leadId: number, targetStage: string) {
    try {
      const res = await fetch(`/api/demos/telecalling/leads/${leadId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage: targetStage }),
      });

      const data = await res.json();
      if (data.success) {
        await fetchLeads();
      }
    } catch (error) {
      console.error("[Direct Stage Move Error]", error);
    }
  }

  // Filter leads
  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      searchQuery === "" ||
      lead.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.prospect_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.lead_no.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.assigned_agent.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === "all" ||
      (selectedCategory === "outbound" && lead.campaign_id === "b2b-outbound") ||
      (selectedCategory === "inbound" && lead.campaign_id === "inbound-care") ||
      (selectedCategory === "tech" && lead.campaign_id === "tech-support") ||
      (selectedCategory === "collections" && lead.campaign_id === "debt-recovery");

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <TelecallingTopBar
        currency={currency}
        onCurrencyToggle={setCurrency}
        onResetDemo={handleResetDemo}
        isResetting={isResetting}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full space-y-6">
        {/* Top Header & Overview */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                Calling Floor &amp; Outbound Pipeline CRM
              </h1>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
              LIVE SYNC
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Drag leads across calling stages to trigger automated QA speech audits &amp; BPO retainer tax invoices via Hostinger SMTP.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={fetchLeads}
            disabled={isLoading}
            className="p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white transition-colors"
            title="Refresh Calling Floor"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin text-cyan-400" : ""}`} />
          </button>
          <button
            type="button"
            onClick={() => setIsProposalModalOpen(true)}
            className="py-2 px-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold text-xs shadow-lg shadow-cyan-500/20 flex items-center gap-1.5 transition-all"
          >
            <Plus className="w-4 h-4" />
            <span>New Campaign Intake</span>
          </button>
        </div>
      </div>

      {/* KPI Metrics Banner */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 font-medium">Total Ingested Leads</span>
            <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
              <Headphones className="w-4 h-4 text-cyan-400" />
            </div>
          </div>
          <div className="text-2xl font-black text-white mt-2">{stats.totalLeads}</div>
          <span className="text-[10px] text-cyan-400 font-medium">Active Dialing Pipeline</span>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 font-medium">Connected Calls</span>
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <PhoneCall className="w-4 h-4 text-blue-400" />
            </div>
          </div>
          <div className="text-2xl font-black text-white mt-2">{stats.connectedCalls}</div>
          <span className="text-[10px] text-blue-400 font-medium">Decision Maker Dialogues</span>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 font-medium">Demos Scheduled</span>
            <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-purple-400" />
            </div>
          </div>
          <div className="text-2xl font-black text-white mt-2">{stats.demosScheduled}</div>
          <span className="text-[10px] text-purple-400 font-medium">Audited Discovery Passes</span>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 font-medium">Contracted Retainers</span>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Award className="w-4 h-4 text-emerald-400" />
            </div>
          </div>
          <div className="text-2xl font-black text-white mt-2">
            {sym}{revenueTotal.toLocaleString()}
          </div>
          <span className="text-[10px] text-emerald-400 font-medium">Active Monthly MRR</span>
        </div>
      </div>

      {/* Search & Program Filter Bar */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-3 rounded-2xl bg-slate-900/60 border border-slate-800">
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search company, prospect, lead no..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-cyan-500"
          />
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
          {[
            { id: "all", label: "All Programs" },
            { id: "outbound", label: "B2B Outbound" },
            { id: "inbound", label: "24/7 Care" },
            { id: "tech", label: "Tech Support" },
            { id: "collections", label: "Soft Collections" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setSelectedCategory(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                selectedCategory === tab.id
                  ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 6-Stage Kanban Board */}
      <TelecallingKanban
        leads={filteredLeads}
        onSelectLead={(lead) => setSelectedDetailLead(lead)}
        onRequestStageAdvance={(lead, targetStage) => setStageModalLead({ lead, targetStage })}
        onDirectStageMove={handleDirectStageMove}
        currency={currency}
      />

      {/* Stage Transition Modal */}
      {stageModalLead && (
        <TelecallingStageModal
          isOpen={!!stageModalLead}
          onClose={() => setStageModalLead(null)}
          lead={stageModalLead.lead}
          targetStage={stageModalLead.targetStage}
          onConfirm={handleConfirmStageTransition}
        />
      )}

      {/* Detail Lead Dossier Modal */}
      {selectedDetailLead && (
        <TelecallingDetailModal
          isOpen={!!selectedDetailLead}
          onClose={() => setSelectedDetailLead(null)}
          lead={selectedDetailLead}
        />
      )}

      {/* New Campaign Intake Modal */}
      {isProposalModalOpen && (
        <CampaignProposalModal
          isOpen={isProposalModalOpen}
          onClose={() => setIsProposalModalOpen(false)}
          currency={currency}
          onSuccess={fetchLeads}
        />
      )}

      </div>
    </div>
  );
}
